/**
 * Fechas que existen de verdad.
 *
 * `Date.parse("2026-02-31")` no falla: corre el día al 3 de marzo. SQLite hace lo mismo con
 * `date('2026-02-31')`. En Rastreo eso es grave: una instalación «del 31 de febrero» abriría
 * el vínculo equipo↔placa el 3 de marzo, y un período inventado pediría otros días al motor.
 * Aquí la fecha se comprueba por partes antes de convertirla (revisión de Fabián al PR #266).
 *
 * Puro: no lee el reloj (constitución, Art. 1). `core/pipeline/analysis-period.ts` valida lo mismo
 * para los períodos de análisis, pero exige segundos y offset de Bogotá; el motor de rastreo acepta
 * cualquier zona y la hora sin segundos, por eso esta versión aparte.
 */

const DATE = /^(\d{4})-(\d{2})-(\d{2})$/u;
const ZONED =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.\d{1,9})?)?(?:Z|([+-])(\d{2}):(\d{2}))$/u;

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

/** Gregoriano, por regla y no con `Date.UTC`, que lleva los años 0 a 99 al siglo XX. */
function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
}

function daysInMonth(year: number, month: number): number {
  return month === 2 && isLeapYear(year) ? 29 : DAYS_IN_MONTH[month - 1]!;
}

function calendarExists(year: number, month: number, day: number): boolean {
  return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth(year, month);
}

/** `AAAA-MM-DD` de un día que existe: ni 31 de febrero ni mes 13. */
export function isCalendarDate(value: string): boolean {
  const match = DATE.exec(value);
  return match !== null && calendarExists(Number(match[1]), Number(match[2]), Number(match[3]));
}

/**
 * Instante ISO 8601 con zona (`Z` o `±hh:mm`), con o sin segundos, como lo pide el motor.
 * Devuelve los milisegundos, o `null` si el formato no es ese o la fecha u hora no existen.
 */
export function parseZonedInstant(value: string): number | null {
  const match = ZONED.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute, second, , offsetHours, offsetMinutes] = match;
  if (!calendarExists(Number(year), Number(month), Number(day))) return null;
  if (Number(hour) > 23 || Number(minute) > 59 || Number(second ?? 0) > 59) return null;
  if (offsetHours !== undefined && (Number(offsetHours) > 14 || Number(offsetMinutes) > 59)) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
}
